import { useState, useCallback } from "react";
import { getDefaultFormData } from "../../../constants/aiPriceData";
import { getDefaultRentalFormData } from "../../../constants/rentalTypes";
import {
  saveFormDataToStorage,
  loadFormDataFromStorage,
  clearFormDataFromStorage,
} from "../utils/aiPriceUtils";

export const useFormData = (prefix = "aiPrice") => {
  // Use appropriate default form data based on prefix
  const getInitialFormData = () => {
    if (prefix === "rental") {
      return getDefaultRentalFormData();
    }
    return getDefaultFormData();
  };

  const [formData, setFormData] = useState(getInitialFormData());

  // Handle form data change with auto-save
  const handleFormDataChange = useCallback(
    (key, value) => {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          [key]: value,
        };

        // Auto-save to localStorage
        saveFormDataToStorage(newFormData, prefix);

        return newFormData;
      });
    },
    [prefix]
  );

  // Handle property type change
  const handlePropertyTypeChange = useCallback(
    (propertyType) => {
      // Reset form data when property type changes but keep the property type
      const newFormData = getInitialFormData();
      newFormData.propertyType = propertyType;
      setFormData(newFormData);

      // Save to localStorage
      saveFormDataToStorage(newFormData, prefix);

      return newFormData;
    },
    [prefix]
  );

  // Handle reset form
  const handleResetForm = useCallback(() => {
    const defaultData = getInitialFormData();
    setFormData(defaultData);
    clearFormDataFromStorage(prefix);
    return defaultData;
  }, [prefix]);

  // Load saved data
  const loadSavedData = useCallback(() => {
    const savedFormData = loadFormDataFromStorage(prefix);
    if (savedFormData) {
      setFormData(savedFormData);
      console.log(`📦 Loaded saved form data (${prefix}):`, savedFormData);
      return savedFormData;
    }
    return null;
  }, [prefix]);

  return {
    formData,
    handleFormDataChange,
    handlePropertyTypeChange,
    handleResetForm,
    loadSavedData,
    setFormData,
  };
};
